import React, { useState } from "react";
import { useSharedValue } from "../components/Constants/constants";
import {
  getUserIdFromLocalStorage,
  getExcelFile,
  getFileBytes,
} from "../API_Endpoints/APIEndpoints";
import { BASE_URL, businessunitName, pythonBaseUrl } from "../App_Settings/app_settings";
import { CircularProgress } from "@mui/material";
import downloadFileIcon from "../assets/new icons/reports/download file.svg";
import excelSvgIcon from "../assets/new icons/reports/excel.svg";
import pdfSvgIcon from "../assets/new icons/reports/pdf.svg";

export const DownloadExcelFile = ({
  fileName,
  title = "Excel",
  businessunit,
  userId,
  organizationId,
  iconSrc,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileName) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading Excel to your local storage. Please delete this file later.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        await getExcelFile(organizationId, userId, businessunit, fileName);
      } catch (error) {
        console.error("Error downloading Excel file:", error);
        alert("Failed to download Excel file. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <a
      href={`https://eformapi.complytaxforms.com/p/s/${fileName}.xltx`}
      download
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleDownloadClick}
      style={{
        // display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 25,
        height: 25,
        textDecoration: "none",
      }}
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "#2aa7e0" }} />
      ) : (
        <img
          title="Download Excel"
          src={iconSrc || excelSvgIcon}
          alt="Excel"
          style={{ height: 22, width: 22, verticalAlign: "middle" }}
        />
      )}
    </a>
  );
};

export const DownloadXMLFile = ({
  fileName,
  fileImportId,
  businessunit,
  title = "XML",
  userId,
  organizationId,
  iconSrc,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileImportId) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading XML to your local storage. Please delete this file later from your local storage.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        const formData = new FormData();
        formData.append("filename", fileImportId);
        formData.append("organization_id", organizationId);
        formData.append("user_id", userId);
        formData.append("business_unit", businessunit || "Demo");

        const response = await fetch(`${pythonBaseUrl}/Excel/DownloadXmlData`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
            accept: "*/*",
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to download XML: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileName || fileImportId}.xml`;
        a.click();
        window.URL.revokeObjectURL(url);

        const downloadTime = new Date();
        localStorage.setItem("downloadTime", downloadTime);
      } catch (error) {
        console.error("Error downloading XML:", error);
        alert("Failed to download XML. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <span
      onClick={handleDownloadClick}
      style={{
        // display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 25,
        height: 25,
        cursor: "pointer",
      }}
      title="Download XML"
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "#2aa7e0" }} />
      ) : (
        <img
          src={iconSrc || downloadFileIcon}
          alt="XML"
          style={{
            width: 22,
            height: 22,
            ...(iconSrc
              ? {}
              : {
                  filter:
                    "invert(55%) sepia(80%) saturate(400%) hue-rotate(170deg) brightness(95%)",
                }),
          }}
        />
      )}
    </span>
  );
};

export const ExcelIcon = ({ onclick }) => {
  return (
    <img
      title="Download Audit Report"
      src={excelSvgIcon}
      alt="Excel"
      onClick={onclick}
      style={{
        height: 22,
        width: 22,
        verticalAlign: "middle",
        marginRight: 6,
        cursor: "pointer",
      }}
    />
  );
};

export const WordIcon = ({ onclick }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#2b579a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      onClick={onclick}
      style={{
        verticalAlign: "middle",
        marginRight: 6,
        cursor: "pointer",
      }}
      title="View Word Document"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
};

export const ViewPDf = ({ fileImportId, fileName, userId, organizationId }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      if (!fileName) {
        alert("Cannot download: missing original file name.");
        return;
      }
      setIsLoading(true);
      const extension = (fileName.split(".").pop() || "pdf").toLowerCase();
      // Match SecureDocumentViewer usage where fileType is often 'uploadfiles'
      const type = "uploadfiles";
      const data = await getFileBytes(
        organizationId,
        userId,
        fileImportId,
        fileName,
        extension,
        type
      );

      const blob = new Blob([data.fileBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in a new tab instead of downloading
      window.open(url, "_blank");
      // Revoke the URL after a short delay to allow the tab to load
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to download PDF from bytes:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div onClick={isLoading ? undefined : handleDownloadPdf}>
      {" "}
      {isLoading ? (
        <CircularProgress size={22} style={{ color: "#2aa7e0", verticalAlign: "middle" }} />
      ) : (
        <img
          title="View PDF"
          src={pdfSvgIcon}
          alt="PDF"
          style={{
            height: 22,
            width: 22,
            verticalAlign: "middle",
            cursor: "pointer",
          }}
        />
      )}
    </div>
  );
};
export const formatDate = (value) => {
  // Handle null, undefined, or empty values
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn("formatDate received invalid date:", value);
    return "-";
  }

  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.toLocaleString("en-US", { day: "2-digit" });
  const year = date.toLocaleString("en-US", { year: "numeric" });
  const hour24 = date.getHours();

  const minute = date.getMinutes();

  const amPm = hour24 < 12 ? "am" : "pm";

  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const formattedHour = hour12 < 10 ? `0${hour12}` : hour12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;

  return `${month} ${day}, ${year} ${formattedHour}:${formattedMinute} ${amPm}`;
};

export const useNotifications = (newNotification) => {
  const { notifications, handleNotificationList } = useSharedValue();

  // const addNotification = (newNotification) => {
  const updatedNotifications = [
    ...(Array.isArray(notifications) ? notifications : []),
    newNotification,
  ];
  handleNotificationList(updatedNotifications);
  // };
};

export const checkIsSuperAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;

    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });

    if (response.ok) {
      const user = await response.json();
      return user?.isSuperAdmin || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking super admin status:", error);
    return false;
  }
};

// Function to check if the user is an admin
export const checkIsAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });
    if (response.ok) {
      const user = await response.json();
      // Assuming userRoles is an array of role objects with a name property
      return user?.userRoles?.some((role) => role.name === "Admin") || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

export const getAllFilesList = async (
  history,
  defaultOrganization,
  defaultBusinessunit,
  defaultUser,
  status = null // Optional status filter (1: uploaded, 2: extracted, 3: in approval, 4: rejected, 5: export)
) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    // Build URL with optional status parameter
    const baseUrl = `${BASE_URL}/FileData/GetAllFiles/${userId}/${organizationId}`;
    const url = status !== null ? `${baseUrl}/?status=${status}` : baseUrl;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      const data = await response.json();
      if (defaultOrganization && defaultOrganization.name !== "") {
        const organization = await getAllFilesListWithOrganization(
          history,
          defaultOrganization,
          status
        );
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return organization.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return organization;
        }
      } else if (defaultUser && defaultUser !== "") {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return data.filter(
            (row) => row.userID === defaultUser && row.businessUnit === defaultBusinessunit
          );
        } else {
          return data.filter((row) => row.userID === defaultUser.id);
        }
      } else {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return data.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return data;
        }
      }
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};
export const getAllFilesListWithOrganization = async (
  history,
  defaultOrganization,
  status = null
) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    // Build URL with optional status parameter
    const baseUrl = `${BASE_URL}/FileData/GetAllFiles/${userId}/${defaultOrganization.id}`;
    const url = status !== null ? `${baseUrl}/${status}` : baseUrl;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};

export const handleLogout = async (history) => {
  // Terminate current session on server BEFORE clearing local storage
  const sessionToken = localStorage.getItem("sessionToken");
  const tokenData = localStorage.getItem("token");
  try {
    if (sessionToken && tokenData) {
      const userData = JSON.parse(tokenData);
      const response = await fetch(`${BASE_URL}/api/SessionManagement/logout-all`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "X-Session-Token": sessionToken,
        },
      });
      if (!response.ok) {
        console.warn("Server-side session termination returned:", response.status);
      }
    }
  } catch (error) {
    console.error("Session logout error:", error);
  } finally {
    // Always clear local storage and redirect, even if server call fails
    localStorage.removeItem("token");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("CopilotToken");
    history.replace("/login");
  }
};

/**
 * Fetch all business units for an organization
 * @param {string} organizationId - Optional organization ID. Uses current user's org if not provided
 * @returns {Promise<Array>} - Array of business units or empty array on error
 */
export const getBusinessUnits = async (organizationId = null) => {
  try {
    const orgId = organizationId || getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;
    const sessionToken = localStorage.getItem("sessionToken");

    const response = await fetch(`${BASE_URL}/BusinessUnits/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sessionToken && { "X-Session-Token": sessionToken }),
      },
    });

    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error("Error fetching business units:", error);
    return [];
  }
};

/**
 * Fetch business units and get admin URL by business unit name
 * @param {string} businessUnitName - Name of the business unit
 * @returns {Promise<string|null>} - Admin URL or null if not found
 */
export const getAdminUrlByBusinessUnit = async (businessUnitName) => {
  try {
    const businessUnits = await getBusinessUnits();
    const matchingUnit = businessUnits.find((unit) => unit.name === businessUnitName);
    return matchingUnit?.adminUrl || null;
  } catch (error) {
    console.error("Error fetching business units for admin URL:", error);
    return null;
  }
};

/**
 * Fetch user's default business unit from user API
 * @returns {Promise<string|null>} - Default business unit name or null if not found
 */
export const getDefaultBusinessUnit = async () => {
  try {
    const { userId, organizationId, token } = getUserIdFromLocalStorage();
    const sessionToken = localStorage.getItem("sessionToken");

    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sessionToken && { "X-Session-Token": sessionToken }),
      },
    });

    if (response.ok) {
      const userData = await response.json();
      const defaultBU = userData.businessUnits?.find((bu) => bu.isDefault === true);
      return defaultBU?.name || null;
    }
    return null;
  } catch (error) {
    console.error("Error fetching default business unit:", error);
    return null;
  }
};

/**
 * Format date to MM/DD/YYYY format for XML generation
 * Handles multiple input formats and avoids timezone conversion issues
 * @param {string} dateStr - Input date string in various formats
 * @returns {string} - Date in MM/DD/YYYY format or original string if parsing fails
 */
export const formatDateForXML = (dateStr) => {
  if (!dateStr) return "";

  // Try to parse MM/DD/YYYY or MM/DD/YY format first (US date format)
  const usDatePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
  const usDateMatch = dateStr.match(usDatePattern);

  if (usDateMatch) {
    const [, month, day, year] = usDateMatch;
    // Convert 2-digit year to 4-digit year
    const fullYear = year.length === 2 ? `20${year}` : year;
    // Pad month and day with leading zeros and return in MM/DD/YYYY format
    const formattedDate = `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${fullYear}`;

    return formattedDate;
  }

  // Try to parse YYYY-MM-DD format and convert to MM/DD/YYYY
  const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const isoDateMatch = dateStr.match(isoDatePattern);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  }

  // Try to format the date if it's in a standard format
  // Use local date methods to avoid timezone conversion issues
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    // Use local date methods to preserve the date as entered
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  } catch (e) {
    return dateStr;
  }
};
